import { XMarkIcon } from "@heroicons/react/24/outline";
import { TierConfig } from "./types";

type TierSectionProps = {
  tiers: {
    tier1: TierConfig;
    tier2: TierConfig;
    tier3: TierConfig;
  };
  onMinChange: (tier: keyof TierSectionProps["tiers"], value: number) => void;
  onRequirementChange: (tier: keyof TierSectionProps["tiers"], index: number, value: string) => void;
  onAddRequirement: (tier: keyof TierSectionProps["tiers"]) => void;
  onRemoveRequirement: (tier: keyof TierSectionProps["tiers"], index: number) => void;
};

const tierOrder: (keyof TierSectionProps["tiers"])[] = ["tier1", "tier2", "tier3"];
const tierLabel: Record<string, string> = { tier1: "Tier 1", tier2: "Tier 2", tier3: "Tier 3" };

export default function TierSection({
  tiers,
  onMinChange,
  onRequirementChange,
  onAddRequirement,
  onRemoveRequirement,
}: TierSectionProps) {
  return (
    <div className="space-y-4">
      {tierOrder.map((tierKey) => {
        const tier = tiers[tierKey];
        return (
          <div key={tierKey} className="rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800/50 px-4 py-3 border-b border-gray-100 dark:border-gray-700">
              <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100">{tierLabel[tierKey]}</h4>
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500 dark:text-gray-400">Min score</label>
                <input
                  type="number"
                  min={0}
                  className="remove-arrow w-20 rounded-lg border border-gray-200 dark:border-gray-600 px-2 py-1.5 text-sm text-gray-800 dark:text-gray-100 bg-white dark:bg-darkbg focus:border-entntblue focus:outline-none focus:ring-1 focus:ring-entntblue/20 transition-colors"
                  value={tier.min}
                  onChange={(e) => onMinChange(tierKey, Number(e.target.value))}
                />
              </div>
            </div>

            <div className="px-4 py-3 space-y-2">
              {tier.requirements.map((req, idx) => (
                <div key={`${tierKey}-${idx}`} className="grid grid-cols-[1fr_36px] items-center gap-2">
                  <input
                    className="rounded-lg border border-gray-200 dark:border-gray-600 px-3 py-2 text-sm text-gray-800 dark:text-gray-100 bg-white dark:bg-darkbg placeholder-gray-400 focus:border-entntblue focus:outline-none focus:ring-1 focus:ring-entntblue/20 transition-colors"
                    value={req}
                    onChange={(e) => onRequirementChange(tierKey, idx, e.target.value)}
                    placeholder="Describe a requirement…"
                  />
                  <button
                    type="button"
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-red-300 dark:border-red-700 text-red-400 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    onClick={() => onRemoveRequirement(tierKey, idx)}
                    aria-label="Remove requirement"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-entntblue/40 px-3 py-2 text-sm font-medium text-entntblue hover:border-entntblue hover:bg-entntblue/5 transition-colors"
                onClick={() => onAddRequirement(tierKey)}
              >
                <span className="text-base leading-none">+</span> Add Requirement
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
